import React, { FC, MutableRefObject, useRef, useState } from 'react';
import { Animated, Easing, StyleProp, Text, View, ViewStyle } from 'react-native';
import { LEVELS, isPhone } from '@helpers';
import SearchInput, { SearchInputProps } from '@components/common/searchInput';
import { ModeSelector } from './toolbar';
import { DisplayMode } from '@v2/pages/common/components/ContextHeader/ContextHeader';
import TouchableElement from '@components/common/touchableElement';
import { font as initialFont } from '@styles/fonts';
import i18n from 'i18n-js';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { scale as initialScale } from '@components/frame';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';

interface SearchInputWithModeSelectorProps
  extends Pick<SearchInputProps, 'C' | 'cancelAnimation' | 'searchRef' | 'searchValue' | 'onSearch' | 'submit' | 'bg' | 'inputName'> {
  showModeSelector: boolean;
  displayMode: DisplayMode;
  setSelectedDisplayMode: (mode: DisplayMode) => void;
  level: (typeof LEVELS)[keyof typeof LEVELS];
  searchInputWithModeSelectorStyle?: StyleProp<ViewStyle>;
  bg?: string;
  disabledModes?: DisplayMode[];
  isPopupMode?: boolean;
  scale?: ScaleFunction;
  font?: typeof initialFont;
  showModeSelectorInPopupMode?: boolean;
  customAnchorRef?: MutableRefObject<View | null>;
}

const SearchInputWithModeSelector: FC<SearchInputWithModeSelectorProps> = ({
  showModeSelector,
  C,
  displayMode,
  setSelectedDisplayMode,
  level,
  cancelAnimation,
  searchRef,
  searchValue,
  searchInputWithModeSelectorStyle,
  bg,
  disabledModes,
  showModeSelectorInPopupMode = false,
  isPopupMode = false,
  scale = initialScale,
  font = initialFont,
  customAnchorRef,
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const showCancel = searchValue || isFocused;

  const fadeAnim = useRef(new Animated.Value(1)).current;

  const onFocus = () => {
    setIsFocused(true);
    Animated.timing(fadeAnim, {
      toValue: 0,
      duration: 300,
      easing: Easing.ease,
      useNativeDriver: false,
    }).start();
  };

  const onBlur = () => {
    setIsFocused(false);
  };

  // In popup mode on mobile, hide ModeSelector to save space
  const shouldShowModeSelector = showModeSelector && !(isPopupMode && isPhone && !showModeSelectorInPopupMode);

  return (
    <View
      ref={customAnchorRef}
      style={[{ width: '100%', marginTop: scale(18), flexDirection: 'row', gap: scale(6), alignItems: 'center' }, searchInputWithModeSelectorStyle]}
    >
      <SearchInput {...{ ...rest, C, searchRef, searchValue, cancelAnimation, setIsFocused, onBlur, onFocus, bg, isPopupMode, scale, font }} />
      {shouldShowModeSelector ? <ModeSelector {...{ C, displayMode, setSelectedDisplayMode, level, bg, disabledModes, scale, font }} /> : null}
      {showCancel && (
        <TouchableElement
          testID={TEST_IDS.searchScreen.cancelSearchButton}
          onPress={() => {
            searchRef?.current?.blur();
            cancelAnimation();
          }}
          hitSlop={5}
        >
          {({ boxStyle, pressHoverColor }) => (
            <Animated.View
              style={[
                boxStyle,
                {
                  paddingLeft: isPopupMode && isPhone ? scale(6) : scale(10),
                  justifyContent: 'center',
                },
              ]}
            >
              <Text style={[{ color: C(pressHoverColor('System/Light/Blue')) }, font.Body]}>{i18n.t('Cancel')}</Text>
            </Animated.View>
          )}
        </TouchableElement>
      )}
    </View>
  );
};

export { SearchInputWithModeSelector };
